<template>
  <div ref="container" class="map-container relative">
    <svg ref="map" :width="width" :height="height" class="rounded-lg" />
    <!-- <VTooltip :show="tooltip.show" :followMouse="true">
      <div>
        <div class="font-bold">{{ tooltip.name }}</div>
        <div>{{ tooltip.subtitle }}</div>
        <div>{{ tooltip.value }}%</div>
        <div>Confidence interval: {{ tooltip.min }}% - {{ tooltip.max }}%</div>
      </div>
    </VTooltip> -->
    <div v-if="error" class="ot-placeholder absolute inset-0">
      Map not available
    </div>
  </div>
</template>

<script>
import api from "@/services/data";

import * as d3 from "d3";
import * as topojson from "topojson-client";
// import countriesGeo from "@/assets/data/countries-110m";
import countriesGeo from "@/assets/data/countries-popcouncil";
import * as IDS from "@/utils/ids";
// import VTooltip from "@/components/VTooltip";

export default {
  name: "CountryMap",
  components: {
    // VTooltip,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    range: {
      type: Array,
      default: null,
    },
    iso: {
      type: String,
      default: "",
    },
    colors: {
      type: [Object, Function],
      default: () => ({ min: "#DBEAFE", max: "#1E3A8A" }),
    },
    highlightColor: {
      type: String,
      default: "black",
    },
    noDataColor: {
      type: String,
      default: "#f3f4f6",
    },
    strokeColor: {
      type: String,
      default: "#ffffff",
    },
    isoId: {
      type: String,
      default: "id",
    },
    duration: {
      type: Number,
      default: 500,
    },
    removeFeatures: {
      type: Array,
      default: () => ["010"],
    },
    projection: {
      type: String,
      default: "geoNaturalEarth1",
    },
    adjustSize: {
      type: Boolean,
      default: false,
    },
    accessor: {
      type: Function,
      default: (d) => d.id,
    },
    backgroundMapColor: {
      type: String,
      default: "#E5E7EB",
    },
    selected: {
      type: [Number, String],
      default: null,
    },
    strokeBackgroundColor: {
      type: String,
      default: "#ffffff",
    },
    scale: {
      type: Number,
      default: 1,
    },
    maxZoom: {
      type: Number,
      default: 16,
    },
    allowZoom: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      width: 1000,
      height: 300,
      loading: false,
      error: false,
      // tooltip: {
      //   show: false,
      //   name: "",
      //   value: 0,
      // },
    };
  },
  computed: {
    max() {
      return d3.max(Object.values(this.data), (d) => d.value);
    },
    colorScale() {
      if (typeof this.colors === "function") {
        return this.colors;
      }
      const interpolator = d3.interpolateRgb(this.colors.min, this.colors.max);
      const scale = d3.scaleLinear().domain([0, this.max]);
      return d3
        .scaleSequential((d) => interpolator(scale(d)))
        .unknown(this.noDataColor);
    },
  },
  watch: {
    data() {
      this.updateColors();
    },
    iso() {
      this.loadTopojson();
    },
    selected() {
      this.highlight();
    },
    scale(newVal, oldVal) {
      const currentTransform = d3.zoomTransform(this.svg.node());
      if (newVal > oldVal) {
        this.zoomToScale(parseInt(currentTransform.k) * 2);
      } else {
        this.zoomToScale(parseInt(currentTransform.k) / 2);
      }
    },
  },
  mounted() {
    this.setSize();
    this.loadTopojson();
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.resize();
      });
      this.resizeObserver.observe(this.$refs.container);
    } else {
      // for IE
      window.addEventListener("resize", this.resize);
    }
  },
  beforeDestroy() {
    if (window.ResizeObserver) {
      this.resizeObserver.unobserve(this.$refs.container);
    } else {
      // for IE
      window.removeEventListener("resize", this.resize);
    }
  },
  methods: {
    draw() {
      const regionsGeo = this.$options.map;

      // country map
      this.featureCollection = topojson.feature(
        regionsGeo,
        regionsGeo.objects.sdr_subnational_boundaries
      );

      // world map
      this.featureWorldCollection = topojson.feature(
        countriesGeo,
        countriesGeo.objects.countries
      );

      // this map will adapt to its container size based on the WIDTH
      // the aspect ratio of the map will be calculated based on the BOUNDS, and the HEIGHT will be set

      const projection = d3["geoNaturalEarth1"]()
        .scale(1)
        .translate([0, 0]);

      const path = d3.geoPath().projection(projection);

      // calculate the bounds of the map
      const b = path.bounds(this.featureCollection);

      if (!this.adjustSize) {
        // DO NOT DO THIS if you want to be limited by the height/width of the container
        // DO THIS only if you want the WIDTH of the container to determine the final size of the map
        // calculate aspect ratio
        const aspectRatio = (b[1][0] - b[0][0]) / (b[1][1] - b[0][1]);
        //set height based on aspect ratio
        this.height = this.width / aspectRatio;
        // END of the scary "DO NOT DO THIS"
      }

      // set the viewBox
      this.svg = d3
        .select(this.$refs.map)
        .attr("viewBox", `0 0 ${this.width} ${this.height}`);

      // some math to calculate the scale in function of the bounds and the width/height
      const scaleFactor = 0.95;

      const s =
        scaleFactor /
        Math.max(
          (b[1][0] - b[0][0]) / this.width,
          (b[1][1] - b[0][1]) / this.height
        );
      // more math for centering the map...
      const t = [
        (this.width - s * (b[1][0] + b[0][0])) / 2,
        (this.height - s * (b[1][1] + b[0][1])) / 2,
      ];

      // and finally scale and translate the projection
      projection.scale(s).translate(t);

      // draw the world map
      this.countries = this.worldMapContainer
        .selectAll("path")
        .data(this.featureWorldCollection.features)
        .join("path")
        .attr("d", path)
        .attr("stroke", this.strokeBackgroundColor)
        .attr("stroke-width", 1)
        .attr("class", "country")
        .attr("fill", this.backgroundMapColor);
      // .attr("id", (d) => `country-${this.accessor(d)}`);

      // draw the country map
      this.regions = this.mapContainer
        .selectAll("path")
        .data(this.featureCollection.features)
        .join("path")
        .attr("d", path)
        .attr("stroke", this.setStrokeColor)
        .attr("stroke-width", 1)
        .attr("class", "region")
        .attr("id", (d) => `region-${this.accessor(d)}`);

      // console.log(
      //   "SHAPEFILE",
      //   this.iso,
      //   this.featureCollection.features.map((e) => ({
      //     code: this.accessor(e),
      //     name: e.properties.REGNAME,
      //   }))
      // );

      // emit events on hover
      this.regions
        .on("mouseover", (event, data) => {
          this.$emit("hover", {
            event,
            data: {
              ...this.data[this.accessor(data)],
              name: data.properties[IDS.COUNTRY_SHAPEFILE_REGION_NAME],
              id: this.accessor(data),
            },
          });
          // this.showTooltip(
          //   data.properties.REGNAME,
          //   this.data[this.accessor(data)]
          // );
        })
        .on("mouseout", () => {
          this.$emit("hover", null);
          // this.hideTooltip();
        })
        .on("click", (event, data) => {
          this.$emit("click", { event, data });
        });

      this.updateColors();

      // SET ZOOM
      if (this.allowZoom) {
        this.zoom = d3
          .zoom()
          .scaleExtent([1, this.maxZoom])
          .on("zoom", this.zoomed);

        this.svg.call(this.zoom);
      }
    },
    zoomed(event) {
      const { transform } = event;
      this.$emit("zoom", transform);
      this.worldMapContainer.attr("transform", transform);
      this.mapContainer.attr("transform", transform);
      this.countries.attr("stroke-width", 1 / transform.k);
      this.regions.attr("stroke-width", 1 / transform.k);
    },
    zoomToScale(scale) {
      if (scale <= 1) {
        this.svg
          .transition()
          .duration(750)
          .call(this.zoom.transform, d3.zoomIdentity);
      } else {
        this.svg
          .transition()
          .duration(750)
          .call(this.zoom.scaleTo, scale);
      }
    },
    setSize() {
      const container = this.$refs.container;
      if (container && !this.error) {
        this.width = container.clientWidth;
        this.height = container.clientHeight;
      }
    },
    resize() {
      const currentWidth = this.width;
      const currentHeight = this.height;
      this.setSize();
      if (currentWidth !== this.width || currentHeight !== this.height) {
        if (this.svg) this.updateMap();
      }
    },
    updateMap() {
      if (!this.error) this.draw();
    },
    updateColors() {
      if (!this.error && this.regions) {
        this.regions
          .transition()
          .duration(this.duration)
          .attr("fill", this.getColor)
          .attr("stroke", this.getStrokeColor);
      }
    },
    createMap() {
      // remove all shapes in case we are painting a new map
      d3.select(this.$refs.map)
        .selectAll("g")
        .remove();

      this.worldMapContainer = d3.select(this.$refs.map).append("g");

      this.mapContainer = d3.select(this.$refs.map).append("g");

      this.draw();
    },
    // showTooltip(name, data) {
    //   this.tooltip.name = name;
    //   this.tooltip.value = data.value;
    //   this.tooltip.show = true;
    //   this.tooltip.min = data.min;
    //   this.tooltip.max = data.max;
    //   this.tooltip.subtitle = data.indicator;
    // },
    // hideTooltip() {
    //   this.tooltip.show = false;
    // },
    highlight() {
      if (this.selected === null)
        this.regions.attr("stroke", this.getStrokeColor).lower();

      this.mapContainer
        .selectAll(`#region-${this.selected}`)
        .attr("stroke", this.highlightColor)
        .raise();
    },
    getColor(d) {
      return this.colorScale(this.data[this.accessor(d)]?.value);
    },
    getStrokeColor(d) {
      // MALDIVES is too small to be seen, we paint the stroke
      if (this.iso === "MDV") {
        return this.getColor(d);
      }
      return this.strokeColor;
    },
    async loadTopojson() {
      try {
        this.loading = true;
        this.error = false;
        this.$options.map = await api.getMap(this.iso);
        this.createMap();
      } catch (err) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
